import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, User, Phone, PlayCircle, Copy, CheckCircle2, Loader2, Search, Home } from 'lucide-react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';

const TutorMyClasses = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await api.get('/sessions/tutor/active');
        setSessions(res.data);
      } catch (err) {
        console.error("Failed to fetch sessions", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) return (
    <div className="h-screen w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex items-center justify-center">
      <Loader2 className="animate-spin text-indigo-500" size={40} />
    </div>
  );

  return (
    <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col overflow-hidden relative">
      
      <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
        <div className="w-full max-w-5xl mx-auto p-4 md:p-10 pb-32">
          
          <header className="mb-6 md:mb-10 text-center md:text-left">
            <h2 className="text-2xl md:text-4xl font-black uppercase italic tracking-tighter text-white">
              {t('active_classes', 'Active Classes')}
            </h2>
            <p className="text-slate-500 font-bold uppercase text-[8px] md:text-[10px] tracking-widest mt-1">
              {t('manage_students', 'Manage your students and initiate sessions')}
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
            {sessions.length > 0 ? sessions.map((session) => (
              <div key={session.id} className="bg-slate-900/80 backdrop-blur-md border border-slate-800 p-5 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] shadow-xl hover:border-indigo-500/50 transition-all flex flex-col min-h-0">
                
                <div className="flex justify-between items-start mb-4 md:mb-6">
                  <div className="space-y-1">
                    <span className="bg-indigo-600/20 text-indigo-400 px-2.5 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest inline-block">
                      {session.job?.subject || t('tutoring', 'Tutoring')}
                    </span>
                    <h3 className="text-lg md:text-2xl font-black text-white leading-tight">
                      {session.student?.fullName}
                    </h3>
                  </div>
                  <div className="p-2 md:p-3 bg-slate-800 rounded-xl text-indigo-400 shrink-0">
                    <User size={18} />
                  </div>
                </div>

                <div className="space-y-3 mb-5 md:mb-8">
                  <div className="flex items-center gap-2 text-slate-400 text-[11px] font-bold">
                    <Phone size={14} className="text-slate-600" />
                    <span>{session.student?.phoneNumber || t('no_phone', 'No phone')}</span>
                  </div>
                  
                  <div className="bg-slate-950/80 border border-slate-800 p-3 md:p-4 rounded-xl md:rounded-2xl">
                    <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                      {t('room_id', 'Room ID')}
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-indigo-400 font-mono text-[10px] md:text-sm break-all leading-tight">
                        {session.id}
                      </code>
                      <button 
                        onClick={() => copyToClipboard(session.id)}
                        className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors text-slate-500 shrink-0"
                      >
                        {copiedId === session.id ? <CheckCircle2 size={14} className="text-green-500" /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => navigate(`/tutor/dashboard?room=${session.id}`)}
                  className="mt-auto w-full bg-indigo-600 hover:bg-indigo-500 py-3.5 rounded-xl md:rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-indigo-900/40 text-[10px] md:text-xs text-white"
                >
                  <PlayCircle size={18} />
                  {t('start_lesson', 'Start Lesson')}
                </button>
              </div>
            )) : (
              <div className="col-span-full bg-slate-900/20 border-2 border-dashed border-slate-800 p-12 md:p-20 rounded-[2rem] text-center">
                <BookOpen size={28} className="mx-auto text-slate-800 mb-4" />
                <p className="text-slate-600 font-black uppercase tracking-widest text-[10px] md:text-sm">
                  {t('no_active_classes', 'No active classes yet.')}
                </p>
                <button 
                  onClick={() => navigate('/tutor/jobs/browse')}
                  className="mt-4 text-indigo-500 font-black uppercase text-[10px] flex items-center justify-center gap-2 mx-auto"
                >
                  <Search size={14} /> {t('browse_jobs', 'Browse Jobs')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <button onClick={() => navigate('/tutor/home')} className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl shadow-blue-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>
    </div>
  );
};

export default TutorMyClasses;